import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, DraftingCompass } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const EASE = [0.22, 1, 0.36, 1];

const Hero = () => {
  const { t } = useLanguage();
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  const headlineLines = t("hero.headline");
  const stats = t("hero.stats");

  return (
    <section id="hjem" ref={sectionRef} className="blueprint-grid relative overflow-hidden bg-white pt-[72px]" data-testid="hero-section">
      <div className="pointer-events-none absolute -top-32 right-0 h-[420px] w-[420px] rounded-full bg-sky-100/60 blur-3xl" />
      <div className="mx-auto grid max-w-7xl gap-14 px-4 py-20 sm:px-8 lg:grid-cols-2 lg:py-28">
        <div className="flex flex-col justify-center">
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="flex items-center gap-2 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-sky-700"
            data-testid="hero-badge"
          >
            <span className="pulse-dot inline-block h-2 w-2 rounded-full bg-sky-600" />
            {t("hero.badge")}
          </motion.p>

          <h1 className="mt-6 font-display text-4xl font-bold leading-[1.08] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl" data-testid="hero-headline">
            {headlineLines.map((line, i) => (
              <span key={`${line}-${i}`} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, delay: 0.65, ease: EASE }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
            data-testid="hero-subheadline"
          >
            {t("hero.sub")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, delay: 0.8, ease: EASE }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <a
              href="#kontakt"
              data-testid="hero-primary-cta"
              className="group inline-flex items-center gap-2 rounded-md bg-sky-600 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-sky-700"
            >
              {t("hero.cta1")}
              <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
            </a>
            <a
              href="#prosjekter"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-7 py-3.5 text-sm font-semibold text-slate-700 transition-colors duration-200 hover:border-sky-600 hover:text-sky-700"
            >
              <DraftingCompass size={16} />
              {t("hero.cta2")}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, delay: 0.95, ease: EASE }}
            className="mt-14 grid grid-cols-1 divide-y divide-slate-200 border-y border-slate-200 sm:grid-cols-3 sm:divide-x sm:divide-y-0"
            data-testid="hero-stats"
          >
            {stats.map((s) => (
              <div key={s.label} className="px-4 py-5 first:pl-0 sm:px-6 sm:first:pl-0">
                <div className="font-display text-xl font-bold text-sky-700">{s.value}</div>
                <div className="mt-1 text-sm font-semibold text-slate-800">{s.label}</div>
                <div className="text-xs text-slate-500">{s.sub}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="relative hidden items-center lg:flex">
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: EASE }}
            className="corner-frame relative w-full overflow-hidden rounded-sm"
          >
            <motion.img
              src="https://images.unsplash.com/photo-1581094488379-6a10d04c0f04?auto=format&fit=crop&w=1200&q=80"
              alt={t("hero.imageAlt")}
              className="h-[520px] w-full object-cover"
              style={{ y: imgY, scale: 1.12 }}
              data-testid="hero-image"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-900/50 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 rounded-sm border border-white/30 bg-white/90 px-4 py-3 backdrop-blur-md" data-testid="hero-image-caption">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-sky-700">{t("hero.captionTitle")}</p>
              <p className="mt-1 text-sm font-medium text-slate-800">{t("hero.captionText")}</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
